/**
 * DV-PROJ-001: Seed Generation Modal Component
 *
 * Modal for creating seed generation jobs with progress tracking
 */

import React, { useState } from 'react';
import { X, Wand2, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import {
  useCreateSeedGenerationJobMutation,
  useSeedGenerationJobQuery
} from '@/hooks/useProjectsQuery';
import type { Project, CharacterProfile, CreateSeedGenerationJobRequest, SeedGenerationJob } from '@/types/projects';

interface SeedGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  character: CharacterProfile;
}

interface FormErrors {
  prompt?: string;
  count?: string;
  guidance?: string;
}

const SeedGenerationModal: React.FC<SeedGenerationModalProps> = ({
  isOpen,
  onClose,
  project,
  character
}) => {
  const [formData, setFormData] = useState<CreateSeedGenerationJobRequest>({
    prompt: '',
    count: 4,
    guidance: 7.5
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  const createJobMutation = useCreateSeedGenerationJobMutation();
  const { data: jobData } = useSeedGenerationJobQuery(currentJobId);

  const currentJob = jobData?.job;

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({ prompt: '', count: 4, guidance: 7.5 });
      setErrors({});
      setCurrentJobId(null);
    }
  }, [isOpen]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Prompt is required
    if (!formData.prompt.trim()) {
      newErrors.prompt = 'Prompt is required';
    } else if (formData.prompt.trim().length < 10) {
      newErrors.prompt = 'Prompt must be at least 10 characters';
    } else if (formData.prompt.trim().length > 1000) {
      newErrors.prompt = 'Prompt must be less than 1000 characters';
    }

    // Count validation
    if (formData.count < 1) {
      newErrors.count = 'Count must be at least 1';
    } else if (formData.count > 10) {
      newErrors.count = 'Count must be no more than 10';
    }

    // Guidance validation
    if (formData.guidance !== undefined) {
      if (formData.guidance < 1) {
        newErrors.guidance = 'Guidance must be at least 1';
      } else if (formData.guidance > 20) {
        newErrors.guidance = 'Guidance must be no more than 20';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await createJobMutation.mutateAsync({
        projectId: project.id,
        characterId: character.id,
        data: {
          prompt: formData.prompt.trim(),
          count: formData.count,
          guidance: formData.guidance
        }
      });

      setCurrentJobId(result.job.id);
    } catch (error) {
      console.error('Failed to create seed generation job:', error);
    }
  };

  // Handle close with job completion check
  const handleClose = () => {
    if (currentJob?.status === 'succeeded') {
      // TODO: Could trigger a refresh of character profile data here
    }
    onClose();
  };

  if (!isOpen) return null;

  // Show job progress if we have a current job
  const showJobProgress = currentJob && currentJobId;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-black/90 backdrop-blur-xl border border-purple-500/20 rounded-lg shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
              <Wand2 className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-light text-white">
                {showJobProgress ? 'Generating Seeds' : 'Generate Seed Images'}
              </h2>
              <p className="text-xs text-white/50 font-mono">
                {character.name} • {project.name}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-white/60 hover:text-white/80 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {showJobProgress ? (
            /* Job Progress View */
            <JobProgressView job={currentJob} onClose={handleClose} />
          ) : (
            /* Form View */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Prompt *
                </label>
                <textarea
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  placeholder="Describe the character appearance, style, and context..."
                  rows={4}
                  className={`w-full px-3 py-2 bg-black/50 border rounded-lg text-white/90 placeholder-white/40
                             focus:outline-none transition-colors font-mono text-sm resize-none ${
                    errors.prompt
                      ? 'border-red-500/50 focus:border-red-500/70'
                      : 'border-white/20 focus:border-purple-500/50'
                  }`}
                  maxLength={1000}
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.prompt ? (
                    <div className="flex items-center gap-2 text-red-400 text-xs">
                      <AlertCircle className="w-3 h-3" />
                      {errors.prompt}
                    </div>
                  ) : (
                    <div />
                  )}
                  <div className="text-xs text-white/40 font-mono">
                    {formData.prompt?.length || 0}/1000
                  </div>
                </div>
              </div>

              {/* Count and Guidance */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Count *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.count}
                    onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) || 1 })}
                    className={`w-full px-3 py-2 bg-black/50 border rounded-lg text-white/90
                               focus:outline-none transition-colors font-mono text-sm ${
                      errors.count
                        ? 'border-red-500/50 focus:border-red-500/70'
                        : 'border-white/20 focus:border-purple-500/50'
                    }`}
                  />
                  {errors.count && (
                    <div className="flex items-center gap-2 mt-1 text-red-400 text-xs">
                      <AlertCircle className="w-3 h-3" />
                      {errors.count}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Guidance *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    step="0.5"
                    value={formData.guidance}
                    onChange={(e) => setFormData({ ...formData, guidance: parseFloat(e.target.value) || 1 })}
                    className={`w-full px-3 py-2 bg-black/50 border rounded-lg text-white/90
                               focus:outline-none transition-colors font-mono text-sm ${
                      errors.guidance
                        ? 'border-red-500/50 focus:border-red-500/70'
                        : 'border-white/20 focus:border-purple-500/50'
                    }`}
                  />
                  {errors.guidance && (
                    <div className="flex items-center gap-2 mt-1 text-red-400 text-xs">
                      <AlertCircle className="w-3 h-3" />
                      {errors.guidance}
                    </div>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {createJobMutation.isError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    Failed to start generation. Please try again.
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={createJobMutation.isPending}
                  className="px-4 py-2 text-sm border border-white/20 text-white/70 rounded
                             hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createJobMutation.isPending || !formData.prompt?.trim()}
                  className="px-4 py-2 text-sm bg-purple-500/20 border border-purple-500/50 text-purple-400 rounded
                             hover:bg-purple-500/30 transition-colors disabled:opacity-50
                             flex items-center gap-2"
                >
                  {createJobMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate Seeds
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Job Progress View Component
interface JobProgressViewProps {
  job: SeedGenerationJob;
  onClose: () => void;
}

const JobProgressView: React.FC<JobProgressViewProps> = ({ job, onClose }) => {
  const getStatusIcon = () => {
    switch (job.status) {
      case 'queued':
        return <Clock className="w-5 h-5 text-yellow-400" />;
      case 'running':
        return <div className="w-5 h-5 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />;
      case 'succeeded':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-white/40" />;
    }
  };

  const getStatusText = () => {
    switch (job.status) {
      case 'queued':
        return 'Queued for processing';
      case 'running':
        return 'Generating seed images...';
      case 'succeeded':
        return `Generated ${job.outputImageUrls?.length || 0} seed images`;
      case 'failed':
        return 'Generation failed';
      default:
        return 'Unknown status';
    }
  };

  return (
    <div className="space-y-6">
      {/* Status */}
      <div className="text-center">
        <div className="flex justify-center mb-3">
          {getStatusIcon()}
        </div>
        <h3 className="text-lg font-light text-white mb-2">
          {getStatusText()}
        </h3>
        <p className="text-sm text-white/60">
          Job ID: {job.id.slice(0, 8)}
        </p>
      </div>

      {/* Job Details */}
      <div className="p-4 bg-black/30 border border-white/10 rounded-lg space-y-3">
        <div>
          <div className="text-xs text-white/60 font-mono uppercase mb-1">Prompt</div>
          <div className="text-sm text-white/80">{job.prompt}</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-white/60 font-mono uppercase mb-1">Count</div>
            <div className="text-sm text-white/80">{job.count}</div>
          </div>
          <div>
            <div className="text-xs text-white/60 font-mono uppercase mb-1">Guidance</div>
            <div className="text-sm text-white/80">{job.guidance}</div>
          </div>
        </div>
      </div>

      {/* Generated Images */}
      {job.status === 'succeeded' && job.outputImageUrls && job.outputImageUrls.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-white/80 mb-3">Generated Images</h4>
          <div className="grid grid-cols-2 gap-2">
            {job.outputImageUrls.map((url, index) => (
              <div key={index} className="aspect-square bg-black/50 border border-white/10 rounded-lg overflow-hidden">
                <img src={url} alt={`Generated seed ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {job.status === 'failed' && job.error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <XCircle className="w-4 h-4" />
            {job.error}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-center pt-4 border-t border-white/10">
        <button
          onClick={onClose}
          className="px-6 py-2 text-sm bg-purple-500/20 border border-purple-500/50 text-purple-400 rounded
                     hover:bg-purple-500/30 transition-colors"
        >
          {job.status === 'succeeded' ? 'Done' : 'Close'}
        </button>
      </div>
    </div>
  );
};

export default SeedGenerationModal;